import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, filter, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IResBookingSupplierListForAdvisingReportParams, ResBookingSupplierListForAdvisingReport } from '../models/res-booking.model';
import { IResApiPackageSupplierRequest, IResApiSupplierContacts, IResApiSupplierRequest, ResPackageSupplierRequestData, ResSupplierContact } from '../models/res-package.model';
import { ReservationServiceTC } from '../models/res-service.models';
import { ResSupplierCancPolicy, ResSupplierDepScheduling } from '@app/res-setup/models/resadm-service.models';

@Injectable({
  providedIn: 'root'
})
export class ReservationSupplierApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  getBookingSupplierListForAdvisingReport(bookingID: number): Observable<ResBookingSupplierListForAdvisingReport[]> {
    return this.callDynamicAPI('booking/getSupplierForAdvisingReport', { BkgID: bookingID }).pipe(
      map<DynamicApiResponse, ResBookingSupplierListForAdvisingReport[]>((response) => response.body.map((data: any) => new ResBookingSupplierListForAdvisingReport(data)))
    );
  }

  getServiceTC(params: { supplierID?: number }): Observable<ReservationServiceTC[]> {
    return this.callDynamicAPI('supplier/getServiceTC', params).pipe(
      map<DynamicApiResponse, ReservationServiceTC[]>((response) => {
        return response.body.map((data: any) => new ReservationServiceTC(data))
      })
    )
  }

  getSupplierCancPolicy(params: { supplierID?: number, serviceID?: number, optionID?: number }): Observable<ResSupplierCancPolicy[]> {
    return this.callDynamicAPI('supplier/getCancPolicy', params).pipe(
      map<DynamicApiResponse, ResSupplierCancPolicy[]>((response) => {
        return response.body.DT0.map((data: any) => {
          data.Details = response.body.DT1.filter((d: any) => d.SupplierCancellationPolicyID === data.SupplierCancellationPolicyID)
          return new ResSupplierCancPolicy(data)
        })
      })
    )
  }

  getSupplierContacts(params: IResApiSupplierContacts): Observable<ResSupplierContact[]> {
    return this.getDynamicAPI('supplier/getSupplierContacts', params).pipe(
      map<DynamicApiResponse, ResSupplierContact[]>((response) => {
        return response.body.map((data: any) => new ResSupplierContact(data))
      })
    );
  }

  getSupplierDepScheduling(params: { supplierID?: number, serviceID?: number, optionID?: number }): Observable<ResSupplierDepScheduling[]> {
    return this.callDynamicAPI('supplier/getDepScheduling', params).pipe(
      map<DynamicApiResponse, ResSupplierDepScheduling[]>((response) => {
        return response.body.map((data: any) => new ResSupplierDepScheduling(data))
      })
    )
  }

  getSupplierRequestData(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierRequestData> {
    return this.getDynamicAPI('supplier/requestGet', params).pipe(
      map<DynamicApiResponse, ResPackageSupplierRequestData>((response) => {
        return new ResPackageSupplierRequestData(response.body)
      })
    );
  }

  manageRequestAdvisingReport(params: IResBookingSupplierListForAdvisingReportParams): Observable<any> {
    return this.postDynamicAPI('booking/manageRequestAdvisingReport', params);
  }

  manageSupplierCancPolicy(params: any): Observable<any> {
    return this.postDynamicAPI('supplier/manageCancPolicy', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageSupplierDepScheduling(params: any): Observable<any> {
    return this.postDynamicAPI('supplier/manageDepScheduling', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  saveSupplierRequest(params: IResApiSupplierRequest) {
    return this.postDynamicAPI('supplierRequest/supplierRequestSave', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
}
